import { useState } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MuiPopover from '@mui/material/Popover';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';

import KnowledgeSourceIcon from '@webclient/components/search-forms/gamification-search/actions/scope-selector/knowledge-source-icon';
import { StyledFormControlLabel } from '@webclient/components/search-forms/gamification-search/actions/scope-selector/style';

import CONFIG from '@webclient/lib/const/user-specific-settings';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';

import { setSearchScopeByUserId } from '@webclient/state/slices/app-settings';
import { selectSearchScope } from '@webclient/state/slices/app-settings/userSettingSelector';

import { SearchScopeType } from '@webclient/shared-types/search-result/types';

import { IconButton, Radio, Text } from '@knowz/knowz-ui';

export default function ScopeSelector() {
  const searchScope = useAppSelector(selectSearchScope);
  const me = useAppSelector((state) => state.auth.me);
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  function handleScopeSelect(
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    if (!me?.uuid) return;
    dispatch(
      setSearchScopeByUserId({
        userId: me.uuid,
        searchScope: value as SearchScopeType,
      }),
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        size="small"
        onClick={handleOpenPopover}
        variant="ghost"
        color="secondary"
      >
        <KnowledgeSourceIcon
          mode={
            searchScope ?? CONFIG.DEFAULT_SPECIFIC_USER_SETTINGS['searchScope']
          }
        />
      </IconButton>

      <MuiPopover
        open={open}
        aria-label="search scope popover"
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        transformOrigin={{
          vertical: 205,
          horizontal: 88,
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{ paper: { sx: { p: 0 } } }}
      >
        <Stack
          sx={{ pb: 2 }}
          gap={2}
        >
          <Text
            sx={{ px: 3, pt: 2.5 }}
            variant="textMd"
            weight="semibold"
          >
            Where to search?
          </Text>

          <Divider />

          <RadioGroup
            sx={{ gap: 2 }}
            value={searchScope}
            onChange={handleScopeSelect}
          >
            <StyledFormControlLabel
              value="web_kb"
              control={<Radio />}
              label="Web & Knowledge Base"
            />

            <StyledFormControlLabel
              value="kb_only"
              control={<Radio />}
              label="Knowledge Base Only"
            />

            <StyledFormControlLabel
              value="web_only"
              control={<Radio />}
              label="Web Only"
            />
          </RadioGroup>
        </Stack>
      </MuiPopover>
    </Box>
  );
}
